import { useContext, useEffect, useReducer, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Helmet } from 'react-helmet';
import { useHotkeys } from 'react-hotkeys-hook';
import dayjs from 'dayjs';

import { Dialog, GlobalContext, SlideOver, Toggle, Utils } from 'lynkco-up-core';
import { GenericObject } from 'lynkco-up-core/dist/types/types';

import { usePanelResize, useUrlQuery } from '../../hooks';
import { BookingDetails } from '../../parts/Bookings';
import { MileageBookingsTable, TotalMileageFilters } from '../../parts/TotalMileage';

import { FiltersState, TerminationText } from '../../parts/Bookings/types';
import { getBookings } from '../../services/bookings';
import { Booking, BookingsQuery, Status as BookingStatus } from '../../services/bookings/types';
import { bookingsReducer, bookingsInitialState, useBookingActions } from './bookingsReducer';
import { getBookingTerminationText, terminateBooking } from '../../parts/Bookings/utils';
import { SelectedBookingContext, SelectedBookingProvider } from './selectedBookingContext';
import { useSelectedBookingActions } from './selectedBookingReducer';
import getBookingById from '../../services/bookings/getBookingById';

enum EndBookingType {
  End = 'end',
  ForceEnd = 'force end',
}

export type SortColumnType = {
  start: number;
  end: number;
};

const dateFormat = 'YYYY-MM-DDTHH:mm:ss.SSSZ';

const defaultStart = dayjs().startOf('day').format(dateFormat);
const defaultEnd = dayjs().endOf('day').format(dateFormat);

function TotalMileage() {
  const { loading, alert } = useContext(GlobalContext);
  const [, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState<BookingsQuery>({
    start: defaultStart,
    end: defaultEnd,
    statuses: [BookingStatus.FINISHED],
  });
  const {
    data = [],
    isLoading,
    isError,
    refetch,
  } = useQuery(['bookings', query], () => getBookings(query), {
    refetchOnWindowFocus: false,
    retry: 2,
  });
  const [isTerminationDialogOpen, setIsTerminationDialogOpen] = useState(false);
  const detailsPanelSize = usePanelResize();
  const [bookingsState, bookingsDispatch] = useReducer(bookingsReducer, bookingsInitialState);
  const [isForceEnd, setIsForceEnd] = useState(false);
  const [isPaymentCancelled, setIsPaymentCancelled] = useState(false);
  const { setBookings, incrementPage, decrementPage } = useBookingActions(bookingsDispatch);
  const { state: selectedBookingState, dispatch: selectedBookingDispatch } = useContext(SelectedBookingContext);
  const { closeDetailsPanel, clearIsBookingTermination, setSelectedBooking, openDetailsPanel } =
    useSelectedBookingActions(selectedBookingDispatch);
  const searchQuery = useUrlQuery();
  const { isDetailsPanelOpen, selectedBooking, isBookingTermination } = selectedBookingState;
  const [pramadeDateRanges, setPramadeDateRanges] = useState<[string, string] | null>();
  const [columnSort, setColumnSort] = useState<SortColumnType>({ start: 1, end: 0 });

  function handleSortChange(field: string, value: number) {
    if (value) setColumnSort({ ...columnSort, [field]: -value });
    else setColumnSort({ ...{ start: 0, end: 0 }, [field]: 1 });
  }

  function handleCloseDialog() {
    setIsTerminationDialogOpen(false);
    clearIsBookingTermination();
  }

  function handleCloseBookingDetails() {
    closeDetailsPanel();

    // Remove bookingId from URL params on booking details close.
    setSearchParams(searchQuery.filter(item => item[0] !== 'bookingId') as GenericObject);
  }

  async function handleTerminateBooking() {
    loading.show();

    const [result] = await terminateBooking(
      selectedBooking,
      (text: string) => {
        alert.show(`${Utils.capitalizeText(text)} booking successful!`, 'success');
        refetch();
      },
      () => {
        alert.show('Booking termination failed!', 'error');
      },
      isForceEnd,
      isPaymentCancelled,
    );

    loading.hide();
    handleCloseDialog();

    if (result) {
      setSelectedBooking(result[0]);
    }
  }

  function handleUpdateFilters(filters: FiltersState) {
    // const statuses = filters.statuses.size ? (Array.from(filters.statuses) as BookingStatus[]) : undefined;
    const startDate = dayjs(filters.startDate);
    const endDate = dayjs(filters.endDate);
    const isValidStartDate = startDate.isValid();
    const isValidEndDate = endDate.isValid();
    const start = isValidStartDate ? startDate.startOf('day').format(dateFormat) : defaultStart;
    const end = isValidEndDate ? endDate.endOf('day').format(dateFormat) : defaultEnd;

    const newQuery: BookingsQuery = {
      userId: filters.lenderId || undefined,
      // lenderId: filters.lenderId || undefined,
      orderReference: filters.orderReference || undefined,
      vin: filters.vin || undefined,
      regNumber: filters.regNr || undefined,
      bookingId: filters.bookingId || undefined,
      digitalKeyId: filters.digitalKeyId || undefined,
      paymentId: filters.paymentId || undefined,
      start,
      end,
      market: filters.country || undefined,
      statuses: [BookingStatus.FINISHED],
    };

    const hasActiveFilters = Object.values(newQuery).filter(Boolean).length > 0;

    if (hasActiveFilters) {
      const searchParams = Object.entries(newQuery).reduce((query, [key, value]) => {
        if (value) {
          query[key] = value;
        }

        return query;
      }, {} as GenericObject);

      setSearchParams(searchParams);
    } else {
      newQuery.start = defaultStart;
      newQuery.end = defaultEnd;
      setSearchParams({});
    }

    setQuery(newQuery);
  }

  function handleUserSearch(customerID: string) {
    closeDetailsPanel();

    const newQuery: BookingsQuery = {
      userId: customerID,
    };
    setQuery(newQuery);
  }

  function handleToggleForceEnd() {
    setIsForceEnd(flag => !flag);
  }

  function handleToggleCapture() {
    setIsPaymentCancelled(!isPaymentCancelled);
    console.log(isPaymentCancelled);
  }

  async function handleBookingId(bookingId: string) {
    loading.show();
    try {
      const result = await getBookingById(bookingId);
      if (result) {
        setSelectedBooking(result);
        openDetailsPanel();
      }
    } catch (e) {
      alert.show('Error loading the booking', 'error');
    }
    loading.hide();
  }

  function sortData() {
    const name = (columnSort.start == 0 && 'end') || 'start';
    const sortedData = data.sort((a: Booking, b: Booking) => {
      const aDate = dayjs(a[name]);
      const bDate = dayjs(b[name]);

      if (aDate.isBefore(bDate)) {
        return columnSort[name];
      } else if (aDate.isAfter(bDate)) {
        return -columnSort[name];
      } else return 0;
    });
    return sortedData;
  }
  useEffect(() => {
    if (!isLoading && !isError) {
      const sortedData = sortData();
      // filted fetched data, leave only bookings with lenderID
      const filteredData = sortedData.filter(booking => {
        const isMatch = booking.lender.snowflakeId === query.userId || query.userId === undefined;
        return isMatch;
      });
      setBookings(filteredData);
    }

    // Toggle next line to add mock bookings
    // setBookings(data.concat(mockBookings as Booking[]));
  }, [data, columnSort]);

  useEffect(() => {
    if (isError) {
      alert.show('Error loading bookings', 'error');
    }
  }, [isError]);

  useHotkeys(`Escape`, () => {
    handleCloseBookingDetails();
  });

  useEffect(() => {
    const shouldOpenTerminationDialog = selectedBooking !== null && isBookingTermination;
    setIsTerminationDialogOpen(shouldOpenTerminationDialog);
  }, [selectedBooking, isBookingTermination]);

  useEffect(() => {
    const bookingQuery = searchQuery.find(item => item[0] === 'bookingId');
    const bookingId = bookingQuery && bookingQuery[1];
    if (bookingId) {
      handleBookingId(bookingId);
    }
  }, []);

  useEffect(() => {
    if (pramadeDateRanges) {
      setPramadeDateRanges(null);
    }
  }, [pramadeDateRanges]);

  return (
    <>
      <Helmet>
        <title>{isDetailsPanelOpen ? `${selectedBooking?.id} - Car Sharing` : 'Bookings - Car Sharing'}</title>
      </Helmet>
      <TotalMileageFilters onUpdateFilters={handleUpdateFilters} dateInputs={pramadeDateRanges} />
      <MileageBookingsTable
        data={bookingsState.displayedBookings}
        isLoading={isLoading}
        paginationStartIndex={
          /** Add 1 to start index because zero based array indexing is meaningless to humans */
          /** If the current page is 1 set the start index to 1 for the same reason */
          bookingsState.currentPage > 1 ? bookingsState.paginationStartIndex + 1 : 1
        }
        paginationEndIndex={Math.min(bookingsState.paginationQuantity, bookingsState.paginationEndIndex)}
        paginationQuantity={bookingsState.paginationQuantity}
        onPaginationNextChange={incrementPage}
        onPaginationPreviousChange={decrementPage}
        onColumnSortChange={handleSortChange}
        columnSort={columnSort}
      />
      <Dialog
        isOpen={isTerminationDialogOpen}
        title="Confirm booking termination"
        icon="warning"
        confirmText="Confirm"
        cancelText="Abort"
        onConfirm={() => handleTerminateBooking()}
        onClose={handleCloseDialog}
        onCancel={handleCloseDialog}>
        <div className="mt-4">
          {getBookingTerminationText(selectedBooking) === TerminationText.End ? (
            <div>
              <div className="flex justify-between items-center p-1">
                <Toggle isToggledOn={isForceEnd} onChange={handleToggleForceEnd} extraClasses="mr-2" />
                <p className="text-xs ml-1.5 align-middle">
                  End bookings by skipping checks on if doors and windows are closed and the car is locked
                </p>
              </div>
              <div className="flex justify-start items-center p-1">
                <Toggle isToggledOn={isPaymentCancelled} onChange={handleToggleCapture} extraClasses="mr-2" />
                <p className="text-xs ml-1.5 align-middle">End booking and cancel payment</p>
              </div>
              <p className="mt-2">{`Are you sure you want to ${
                isForceEnd ? EndBookingType.ForceEnd : EndBookingType.End
              } the selected booking?`}</p>
            </div>
          ) : (
            <>
              <p>{`Are you sure you want to ${TerminationText.Cancel} the selected booking?`}</p>
            </>
          )}
        </div>
      </Dialog>
      <SlideOver
        isOpen={isDetailsPanelOpen}
        onCancel={handleCloseBookingDetails}
        onClose={handleCloseBookingDetails}
        size={detailsPanelSize}>
        {selectedBooking && <BookingDetails onUserSearch={handleUserSearch} booking={selectedBooking} />}
      </SlideOver>
    </>
  );
}

function TotalMileageWrapper() {
  return (
    <SelectedBookingProvider>
      <TotalMileage />
    </SelectedBookingProvider>
  );
}

export default TotalMileageWrapper;
